import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { categories, comics } from "@/mocks/comics";
import ComicCard from "./components/ComicCard";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");
  const [statusFilter, setStatusFilter] = useState<"all" | "ongoing" | "completed">("all");

  const currentCategory = categories.find((cat) => cat.slug === slug);

  const filteredComics = useMemo(() => {
    let result = slug
      ? comics.filter((c) => {
          const cat = categories.find((cat2) => cat2.id === c.category);
          return cat?.slug === slug;
        })
      : [...comics];

    if (statusFilter !== "all") {
      result = result.filter((c) => c.status === statusFilter);
    }

    if (sortBy === "latest") {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === "popular") {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === "rating") {
      result.sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [slug, sortBy, statusFilter]);

  if (!currentCategory && slug) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              分类未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              您查找的分类不存在
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = currentCategory
    ? `${currentCategory.name}漫画 - 免费下拉式阅读 - 布丁漫画网`
    : "全部漫画 - 免费下拉式阅读 - 布丁漫画网";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: currentCategory?.description || "布丁漫画网全部漫画，免费下拉式阅读",
    url: `https://www.puddingcomic.com/category/${slug || "all"}`,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: filteredComics.length,
      itemListElement: filteredComics.map((comic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Book",
          name: comic.title,
          author: { "@type": "Person", name: comic.author },
        },
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(currentCategory
                  ? [{ label: currentCategory.name }]
                  : [{ label: "全部漫画" }]),
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory ? currentCategory.name : "全部漫画"}
              </h1>
              <p className="text-sm text-foreground-500">
                {currentCategory?.description || "浏览布丁漫画网全部漫画作品"}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">状态：</span>
                <div className="flex items-center bg-background-100 rounded-full p-1">
                  {[
                    { value: "all", label: "全部" },
                    { value: "ongoing", label: "连载中" },
                    { value: "completed", label: "已完结" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        setStatusFilter(opt.value as "all" | "ongoing" | "completed")
                      }
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        statusFilter === opt.value
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                <div className="flex items-center bg-background-100 rounded-full p-1">
                  {[
                    { value: "latest", label: "最新" },
                    { value: "popular", label: "最热" },
                    { value: "rating", label: "评分" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        setSortBy(opt.value as "latest" | "popular" | "rating")
                      }
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === opt.value
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-bookmark-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">暂无漫画</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}